import React, { useEffect } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { fetchData } from 'reducers/dataTable';
import { openModal } from 'reducers/modals';
// components
import { FormattedMessage } from 'react-intl';
import DataTable from 'components/DataTable/DataTable';
import { CustomColumn } from 'components/DataTable/Columns';
import NothingBox from 'components/NothingBox';
import DropdownButton from 'components/DropdownButton';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import DeliveryFormModal from './DeliveryFormModal';
// endpoints
import { createFeedDelivery, updateFeedDelivery, deleteFeedDelivery } from 'endpoints/feedDeliveries';
// utils
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import { formatNumber, showToastrMessage } from 'utils';
import { formatDateForUser } from 'utils/regionalDateHelper';
import { lbsToDisplay, weightUnit } from '../utils';
// selectors
import { getCurrentCountryCode } from 'selectors/regionalDateSelectors';
// hooks
import useDataTableController from 'hooks/useDataTableController';

const labels = {
  date: <FormattedMessage id="general.deliveryDate" />,
  group: <FormattedMessage id="general.group" />,
  bin: <FormattedMessage id="component.dataTable.headers.binID" />,
  ration: <FormattedMessage id="general.ration" />,
  medications: <FormattedMessage id="general.medications" />,
  qty: <FormattedMessage id="component.feedDeliveries.qty" />,
};

const DeliveriesTab = ({
  farmId,
  measurementSystem,
  countryCode,
  fetchData,
  isLoading,
  meta,
  resources,
  reqParams,
  openModal,
}) => {
  const { onPageChange, onPerPageChange, onSortChange } = useDataTableController(fetchData, reqParams);
  const { sort, page, per_page } = reqParams;

  const paginationProps = {
    totalItems: meta.total,
    currentPage: page,
    perPage: per_page,
    onPageChange,
    onPerPageChange,
  };

  const loadData = () => {
    fetchData(reqParams).catch(toastResponseErrors);
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleCreate = async (resource) => {
    await createFeedDelivery(farmId, resource);
    showToastrMessage('component.toastr.feedDelivery.created');
    loadData();
  };

  const handleUpdate = (delivery) => async (resource) => {
    await updateFeedDelivery(farmId, delivery.id, resource);
    showToastrMessage('component.toastr.feedDelivery.updated');
    loadData();
  };

  const handleDelete = async (delivery) => {
    try {
      await deleteFeedDelivery(farmId, delivery.id);
      showToastrMessage('component.toastr.feedDelivery.deleted');
      loadData();
    } catch (error) {
      toastResponseErrors(error);
    }
  };

  const openFormModal = (delivery) => {
    openModal(
      <DeliveryFormModal
        farmId={farmId}
        delivery={delivery}
        measurementSystem={measurementSystem}
        onSubmit={delivery ? handleUpdate(delivery) : handleCreate}
      />
    );
  };

  // Deletes require a confirmation warning (EPN-3041)
  const openDeleteModal = (delivery) => {
    openModal((
      <ConfirmationModal
        title={<FormattedMessage id="component.feedDeliveries.deleteDelivery" />}
        actionBtnLabel={<FormattedMessage id="general.button.delete" />}
        actionBtnProps={{ red: true }}
        warningMessage={<FormattedMessage id="component.feedDeliveries.deleteDeliveryWarning" />}
        handleConfirm={() => handleDelete(delivery)}
      />
    ));
  };

  const renderDateColumn = ({ delivery_date }) => (
    <CustomColumn label={labels.date}>
      {formatDateForUser(delivery_date, countryCode)}
    </CustomColumn>
  );

  renderDateColumn.propTypes = { delivery_date: T.string };

  const renderGroupColumn = ({ pig_group_name }) => (
    <CustomColumn label={labels.group}>{pig_group_name}</CustomColumn>
  );

  renderGroupColumn.propTypes = { pig_group_name: T.string };

  const renderBinColumn = ({ bin_name }) => (
    <CustomColumn label={labels.bin}>{bin_name}</CustomColumn>
  );

  renderBinColumn.propTypes = { bin_name: T.string };

  const renderRationColumn = ({ feed_ration_name }) => (
    <CustomColumn label={labels.ration}>{feed_ration_name}</CustomColumn>
  );

  renderRationColumn.propTypes = { feed_ration_name: T.string };

  const renderMedicationsColumn = ({ medications }) => (
    <CustomColumn label={labels.medications}>
      {medications?.length
        ? medications.map(({ name }) => name).join(', ')
        : <FormattedMessage id="general.notAvail" />}
    </CustomColumn>
  );

  renderMedicationsColumn.propTypes = { medications: T.array };

  const renderQtyColumn = ({ quantity }) => (
    <CustomColumn label={labels.qty}>
      {formatNumber(lbsToDisplay(quantity, measurementSystem))} {weightUnit(measurementSystem)}
    </CustomColumn>
  );

  renderQtyColumn.propTypes = { quantity: T.number };

  const renderActionsColumn = (delivery, { rowIndex }) => (
    <div className="collapsible-value button-column">
      <DropdownButton
        idKey={`${delivery.id}-${rowIndex}`}
        wide
        buttonType="small light-gray"
        label={<FormattedMessage id="general.button.edit" />}
        onClick={() => openFormModal(delivery)}
        dropDownData={[{
          label: <FormattedMessage id="general.button.delete" />,
          onClick: () => openDeleteModal(delivery),
        }]}
        customClass="show-for-large"
      />
    </div>
  );

  const columns = [
    { label: labels.date, flex: '1 0 110px', renderer: renderDateColumn, sortKey: 'delivery_date' },
    { label: labels.group, flex: '1 0 130px', renderer: renderGroupColumn },
    { label: labels.bin, flex: '1 0 110px', renderer: renderBinColumn },
    { label: labels.ration, flex: '1 0 120px', renderer: renderRationColumn },
    { label: labels.medications, flex: '1 1 160px', renderer: renderMedicationsColumn },
    { label: labels.qty, flex: '1 0 100px', renderer: renderQtyColumn },
    { label: '', flex: '0 0 135px', renderer: renderActionsColumn, fixed: true },
  ];

  return (
    <div className="deliveries-tab">
      <div className="tab-actions">
        <button className="button small primary" onClick={() => openFormModal(null)}>
          <i className="fa fa-plus mr-5" />
          <FormattedMessage id="component.feedDeliveries.addDelivery" />
        </button>
      </div>

      <DataTable
        data={resources}
        columns={columns}
        sort={sort}
        onSortChange={onSortChange}
        isLoading={isLoading}
        paginationProps={paginationProps}
      />

      <NothingBox display={!isLoading && resources.length === 0}>
        <FormattedMessage tagName="h1" id="component.feedDeliveries.noDeliveries" />
      </NothingBox>
    </div>
  );
};

DeliveriesTab.propTypes = {
  farmId: T.number.isRequired,
  measurementSystem: T.oneOf(['imperial', 'metric']),
  countryCode: T.string,
  fetchData: T.func.isRequired,
  isLoading: T.bool.isRequired,
  meta: T.object,
  resources: T.array.isRequired,
  reqParams: T.object.isRequired,
  openModal: T.func.isRequired,
};

export default connect(
  (state) => ({
    resources: state.dataTable.feedDeliveries.resources,
    reqParams: state.dataTable.feedDeliveries.params,
    isLoading: state.dataTable.feedDeliveries.isLoading,
    meta: state.dataTable.feedDeliveries.meta,
    countryCode: getCurrentCountryCode(state),
  }), (dispatch, { farmId }) => ({
    fetchData: (query) => dispatch(fetchData(`/feed_deliveries/farms/${farmId}/deliveries`, 'feedDeliveries', query)),
    openModal: (modal) => dispatch(openModal(modal)),
  }),
)(DeliveriesTab);
